import * as React from "react";
import * as React44ms from "../../../Util/form-utils/react-44ms";

import { ACountry } from "../../model";

import {
  AutocompleteInputExpects,
  AutocompleteInputFormController,
} from "../ui2023";
import {
  LeanManualAddress,
  Register_LeanManualAddress_Onto_ManualAddress,
} from "./lean-manual-address-2023";

import { ManualAddressExpects } from "./manual-address-2023";
import {
  DataSourceAddressAutocompleteQueryProviderProps,
  useDataSourceAddressAutocompleteController_44,
  useDataSourceAddressAutocompleteQuery_44,
} from "../../providers/data-source-address-autocomplete";
import {
  useDataSourceAddressParseController,
  useDataSourceAddressParseQuery_44,
} from "../../providers/data-source-address-parse";
import { useLocalizationProvider_44 } from "../../providers/localization-provider";

interface AddressAutocompleteFormLogicPureProps {
  ref: React44ms.Api.TypedRef<AutocompleteInputExpects>;
  yieldToRefAddressManual: React44ms.Api.TypedRef<ManualAddressExpects>;
  required?: boolean;
  shouldRestrictToCountry?: ACountry;
}
export const AddressAutocompleteFormLogicPure: React44ms.Api.FunctionComponent<
  AddressAutocompleteFormLogicPureProps
> = (props) => {
  const addressAutocomplete = useDataSourceAddressAutocompleteQuery_44();
  const addressAutocomplete_asController =
    useDataSourceAddressAutocompleteController_44();
  const addressParse = useDataSourceAddressParseQuery_44();
  const addressParse_asController = useDataSourceAddressParseController();
  const { countryCode } = useLocalizationProvider_44();

  const [value, setValue] = React44ms.Api.useState("");
  const [selectedFromAutocomplete, setSelectedFromAutocomplete] =
    React44ms.Api.useState(false);
  const [hasMigratedAddress, setHasMigratedAddress] =
    React44ms.Api.useState(false);

  const [error, setError] =
    React44ms.Api.useState<AutocompleteInputExpects["error"]>(undefined);

  const [needsValidationAfterABlur, setNeedsValidationAfterABlur] =
    React44ms.Api.useState(false);

  const suggestions = React44ms.Api.useMemo(
    () => addressAutocomplete?.data?.data || [],
    [addressAutocomplete?.data?.data]
  );

  const isLoadingDebounces = React44ms.Api.useMemo(() => {
    return addressAutocomplete.status === "loading";
  }, [addressAutocomplete.status]);
  const isLoadingToValidateTheAddressSelection = React44ms.Api.useMemo(() => {
    return addressParse.status === "loading";
  }, [addressParse.status]);

  const validation = React44ms.Api.useCallback(() => {
    if (props.required && !selectedFromAutocomplete) {
      setError("This field is required.");
      return false;
    }
    setError(undefined);
    return true;
  }, [props.required, selectedFromAutocomplete]);

  React44ms.Api.useEffect(() => {
    if (needsValidationAfterABlur) {
      const isTheAddressValid = validation();
      if (!isTheAddressValid) {
        setValue("");
      }
      setNeedsValidationAfterABlur(false);
    }
  }, [needsValidationAfterABlur]);

  React44ms.Api.useEffect(
    () => {
      const requestSuggestions: DataSourceAddressAutocompleteQueryProviderProps =
        {
          addressToAutocomplete: value,
          countryCode,
        };
      addressAutocomplete_asController.request(requestSuggestions);
    },
    [addressAutocomplete_asController, value, countryCode],
    {
      debounce: 500,
    }
  );

  React44ms.Api.useEffect(
    () => {
      if (!value || value.length < 1) {
        return;
      }
      const requestAddressParse = {
        countryCode,
        addressToParse: !!selectedFromAutocomplete ? value : "",
      };
      addressParse_asController.request(requestAddressParse);
    },
    [addressParse_asController, selectedFromAutocomplete, value, countryCode],
    {
      debounce: 500,
    }
  );

  React44ms.Api.useEffect(() => {
    const hasAlreadyBeenParsed = hasMigratedAddress;
    const isThisAddressNotEmpty = !!value;
    const hasThisAddressBeenSelectedFromSuggestions =
      !!selectedFromAutocomplete;
    const isTheAddressThatHasBeenParsedThisAddress =
      addressParse?.originalRequest?.addressToParse === value;
    const parsedAddress = addressParse?.data?.data;
    const hasThisAddressBeenParsed =
      !hasAlreadyBeenParsed &&
      isThisAddressNotEmpty &&
      hasThisAddressBeenSelectedFromSuggestions &&
      isTheAddressThatHasBeenParsedThisAddress &&
      !!parsedAddress;
    const thisAddressHasAlreadyBeenParsedThankYou = !hasThisAddressBeenParsed;
    if (thisAddressHasAlreadyBeenParsedThankYou) {
      return;
    }

    const correspondingCountryEntry = Object.entries({
      AUSTRALIA: ACountry.Australia,
      "NEW ZEALAND": ACountry.NewZealand,
    }).find(([key, _value]) => key === parsedAddress.country_id);
    const country = correspondingCountryEntry
      ? correspondingCountryEntry[1]
      : "";

    if (
      props.shouldRestrictToCountry &&
      country !== props.shouldRestrictToCountry
    ) {
      const presentationLabelOfCountry = Object.entries({
        [ACountry.Australia]: "Australia",
        [ACountry.NewZealand]: "New Zealand",
        [ACountry.Singapore]: "Singapore",
      }).find(([key, _value]) => key === props.shouldRestrictToCountry);
      setError(`${presentationLabelOfCountry} addresses only`);
      setHasMigratedAddress(true);
      setSelectedFromAutocomplete(false);
      return;
    }

    const leanManualAddress: LeanManualAddress = {
      addressLine1: parsedAddress.street0 || "",
      addressLine2: parsedAddress.street1 || "",
      suburb: parsedAddress.city || "",
      postcode: parsedAddress.postcode || "",
      state: parsedAddress.region?.toUpperCase() || "",
      country: country,
    };

    Register_LeanManualAddress_Onto_ManualAddress({
      leanManualAddress,
      formRefManualAddress: props.yieldToRefAddressManual,
    });

    setHasMigratedAddress(true);
  }, [
    hasMigratedAddress,
    value,
    selectedFromAutocomplete,
    addressAutocomplete?.data?.data,
    addressAutocomplete?.status,
    addressParse?.data?.data,
    addressParse?.status,
    addressParse?.originalRequest,
  ]);

  const handleFocus = React44ms.Api.useCallback(() => {
    setError(undefined);
  }, []);
  const handleChange: React44ms.Api.input["onChange"] =
    React44ms.Api.useCallback((event) => {
      setValue(event.target.value);
      setSelectedFromAutocomplete(false);
      setHasMigratedAddress(false);
      setError(undefined);
    }, []);
  const handleSuggestionClick: React44ms.Api.input["onChange"] =
    React44ms.Api.useCallback((event) => {
      let inputValue = event.target.value;
      if (
        inputValue &&
        typeof inputValue !== "string" &&
        inputValue.currentValue
      ) {
        inputValue = inputValue.currentValue;
      }

      const suggestionSelected = inputValue as string;
      setSelectedFromAutocomplete(true);
      setValue(suggestionSelected);
      setError(undefined);
      setHasMigratedAddress(false);
    }, []);
  const handleBlur = React44ms.Api.useCallback(() => {
    /**
     * handleSuggestionClick will also fire a blur event.
     * React proper will render between events,
     * Externals will not.
     *
     * Technically it's most correct either way to
     * setup validation "onBlur" to happen as an effect
     * for this particular thing,
     *
     * Basically handleSuggestionClick races with handleBlur.
     */
    setNeedsValidationAfterABlur(true);
  }, []);
  const handleSubmit: React44ms.Api.form["onSubmit"] =
    React44ms.Api.useCallback(() => {
      React44ms.Api.submit(validation() as unknown as true);
    }, [validation]);

  const outputValue: AutocompleteInputExpects = React44ms.Api.useMemo(
    () => ({
      currentValue: value,
      isDebouncedLoadingInProgress: isLoadingDebounces,
      suggestions,
      hasBeenSelectedFromTheAutocomplete: selectedFromAutocomplete,
      error,
      isValidatingAddressSelectionInProgress:
        isLoadingToValidateTheAddressSelection,
      required: !!props.required,
    }),
    [
      value,
      isLoadingDebounces,
      suggestions,
      selectedFromAutocomplete,
      error,
      isLoadingToValidateTheAddressSelection,
      props.required,
    ]
  );

  return (
    <form React44ms_onSubmit={handleSubmit}>
      <input
        onFocus={handleFocus}
        React44ms_onChange={handleChange}
        onBlur={handleBlur}
      />

      <input
        name={React44ms.Api.Convenience.element.name<AutocompleteInputFormController>(
          "handleSelectASuggestion"
        )}
        React44ms_onChange={handleSuggestionClick}
      />

      <output React44ms_value={outputValue} />
    </form>
  );
};

export const AddressAutocompleteFormLogic = React44ms.React.Reactify(
  AddressAutocompleteFormLogicPure
);
